import React, { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { BattleAnimationManager, AnimationCommand, AnimationEventCallback, QueueCompleteCallback } from '../game/BattleAnimationManager';
import { ModelManager } from '../game/ModelManager';
import { EnvironmentModelManager } from '../game/EnvironmentModelManager';
import { ModelAnimationController } from '../game/animation/ModelAnimationController';
import { defaultConfig } from '../game/gameConfig';
import EffectsCanvas, { EffectsCanvasHandle } from './EffectsCanvas/EffectsCanvas';

export interface BattleCanvasHandle {
  playBattleAnimation: (animations: AnimationCommand[], roundResult: any) => void;
  resetBattle: () => void;
  getCharacterPosition: (characterId: 'player' | 'opponent') => { x: number; y: number } | null;
}

interface BattleCanvasProps {
  width?: number;
  height?: number;
  onAnimationEvent?: AnimationEventCallback;
  onQueueComplete?: QueueCompleteCallback;
  playerModel?: {
    modelUrl: string;
    textures?: { diffuse?: string; weapon?: string };
    animationMapping?: any;
    baseUrl?: string;
    scale?: number;
    eventFrames?: Record<string, Record<string, number>>;
  };
  opponentModel?: {
    modelUrl: string;
    textures?: { diffuse?: string; weapon?: string };
    animationMapping?: any;
    baseUrl?: string;
    scale?: number;
    eventFrames?: Record<string, Record<string, number>>;
  };
  environmentModel?: {
    modelUrl: string;
    textures?: string[];
    scale?: number;
    position?: { x: number; y: number; z: number };
  };
}

const BattleCanvas = forwardRef<BattleCanvasHandle, BattleCanvasProps>((props, ref) => {
  const { width = 800, height = 600 } = props;
  const mountRef = useRef<HTMLDivElement>(null);
  const animationManagerRef = useRef<BattleAnimationManager | null>(null);
  const modelManagerRef = useRef<ModelManager | null>(null);
  const environmentManagerRef = useRef<EnvironmentModelManager | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const frameIdRef = useRef<number | null>(null);
  const playerControllerRef = useRef<ModelAnimationController | null>(null);
  const opponentControllerRef = useRef<ModelAnimationController | null>(null);
  const environmentModelRef = useRef<THREE.Group | null>(null);

  useImperativeHandle(ref, () => ({
    playBattleAnimation: (animations: AnimationCommand[], roundResult: any) => {
      if (animationManagerRef.current) {
        animationManagerRef.current.queueAnimations(animations, roundResult);
      }
    },
    getCharacterPosition: (characterId: 'player' | 'opponent') => {
      const controller = characterId === 'player' ? playerControllerRef.current : opponentControllerRef.current;
      if (!controller || !cameraRef.current || !mountRef.current) return null;

      const canvasRect = mountRef.current.getBoundingClientRect();
      const position3D = controller.getPosition().clone();
      position3D.y += 3.5; // Смещаем текст чуть выше модели (было 1.5)
      const vector = position3D.project(cameraRef.current);

      // Convert normalized device coordinates to screen coordinates relative to the canvas
      const x = (vector.x * 0.5 + 0.5) * canvasRect.width;
      const y = (vector.y * -0.5 + 0.5) * canvasRect.height;

      // Return coordinates relative to the canvas, as EffectsCanvas is now a child
      return { x, y };
    },
    resetBattle: () => {
      if (animationManagerRef.current) {
        animationManagerRef.current.reset();
      }
    }
  }));

  useEffect(() => {
    if (!mountRef.current) return;
    
    // Prevent double initialization
    if (animationManagerRef.current) return;

    const config = defaultConfig;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(config.scene.background);
    scene.fog = new THREE.Fog(config.scene.fog.color, config.scene.fog.near, config.scene.fog.far);
    sceneRef.current = scene;
    
    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      config.scene.camera.fov,
      width / height,
      0.1,
      1000
    );
    camera.position.set(
      config.scene.camera.position.x,
      config.scene.camera.position.y,
      config.scene.camera.position.z
    );
    cameraRef.current = camera;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Controls setup
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.maxPolarAngle = Math.PI / 2;
    controls.target.set(
      config.scene.camera.lookAt.x,
      config.scene.camera.lookAt.y,
      config.scene.camera.lookAt.z
    );
    controls.minDistance = 5;
    controls.maxDistance = 20;
    controlsRef.current = controls;

    // Camera auto-return functionality
    const baseCameraPosition = new THREE.Vector3(
      config.scene.camera.position.x,
      config.scene.camera.position.y,
      config.scene.camera.position.z
    );
    const baseCameraTarget = new THREE.Vector3(
      config.scene.camera.lookAt.x,
      config.scene.camera.lookAt.y,
      config.scene.camera.lookAt.z
    );

    let isInteracting = false;
    let returnToBase = false;
    const returnSpeed = config.scene.camera.returnSpeed;

    controls.addEventListener('start', () => {
      isInteracting = true;
      returnToBase = false;
    });

    controls.addEventListener('end', () => {
      isInteracting = false;
      returnToBase = true;
    });

    // Lighting - такое же как в админке
    // Ambient light для базового освещения
    const ambientLight = new THREE.AmbientLight(0xffffff, 5.6);
    scene.add(ambientLight);
    
    // Directional light для теней и объема
    const directionalLight = new THREE.DirectionalLight(0xffffff, 10.0);
    directionalLight.position.set(5, 10, 5);
    directionalLight.castShadow = true;
    
    // Настройка теней
    directionalLight.shadow.camera.near = 0.1;
    directionalLight.shadow.camera.far = 50;
    directionalLight.shadow.camera.left = -10;
    directionalLight.shadow.camera.right = 10;
    directionalLight.shadow.camera.top = 10;
    directionalLight.shadow.camera.bottom = -10;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    
    // Настройки для self-shadowing
    directionalLight.shadow.bias = -0.0005;
    directionalLight.shadow.normalBias = 0.02;
    
    // Направляем свет в центр сцены
    directionalLight.target.position.set(0, 0, 0);
    scene.add(directionalLight);
    scene.add(directionalLight.target);

    // Floor and grid removed - environment model will provide the ground

    // Create managers
    const animationManager = new BattleAnimationManager(scene, props.onAnimationEvent, props.onQueueComplete);
    animationManagerRef.current = animationManager;
    
    const modelManager = new ModelManager();
    modelManagerRef.current = modelManager;
    animationManager.setModelManager(modelManager);

    const environmentManager = new EnvironmentModelManager();
    environmentManagerRef.current = environmentManager;

    // Load environment model if provided
    const loadEnvironmentModel = async () => {
      if (!props.environmentModel?.modelUrl) return;

      try {
        console.log('[BattleCanvas] Loading environment model...');
        const envModel = await environmentManager.loadEnvironmentModel(
          'battle-environment',
          props.environmentModel.modelUrl,
          props.environmentModel.textures || [],
          props.environmentModel.scale || 1,
          props.environmentModel.position
        );

        // Remove old environment model if exists
        if (environmentModelRef.current) {
          scene.remove(environmentModelRef.current);
        }

        // Add new environment model to scene
        environmentModelRef.current = envModel.model;
        scene.add(envModel.model);
        
        console.log('[BattleCanvas] Environment model loaded successfully');
      } catch (error) {
        console.error('[BattleCanvas] Failed to load environment model:', error);
      }
    };

    // Setup combatants
    const setupCombatants = async () => {
      try {
        // Create model controllers
        const playerPos = new THREE.Vector3(
          config.characters.player.position.x,
          config.characters.player.position.y,
          config.characters.player.position.z
        );
        const playerRot = new THREE.Euler(
          config.characters.player.rotation.x,
          config.characters.player.rotation.y,
          config.characters.player.rotation.z
        );
        const playerController = new ModelAnimationController(
          'player',
          modelManager,
          scene,
          playerPos,
          playerRot
        );
        playerControllerRef.current = playerController;

        const opponentPos = new THREE.Vector3(
          config.characters.opponent.position.x,
          config.characters.opponent.position.y,
          config.characters.opponent.position.z
        );
        const opponentRot = new THREE.Euler(
          config.characters.opponent.rotation.x,
          config.characters.opponent.rotation.y,
          config.characters.opponent.rotation.z
        );
        const opponentController = new ModelAnimationController(
          'opponent',
          modelManager,
          scene,
          opponentPos,
          opponentRot
        );
        opponentControllerRef.current = opponentController;

        // Initialize controllers with provided models or placeholders
        await Promise.all([
          playerController.initialize(
            props.playerModel?.scale || config.characters.player.scale,
            props.playerModel?.modelUrl,
            props.playerModel?.textures,
            props.playerModel?.animationMapping,
            props.playerModel?.baseUrl,
            props.playerModel?.eventFrames
          ),
          opponentController.initialize(
            props.opponentModel?.scale || config.characters.opponent.scale,
            props.opponentModel?.modelUrl,
            props.opponentModel?.textures,
            props.opponentModel?.animationMapping,
            props.opponentModel?.baseUrl,
            props.opponentModel?.eventFrames
          )
        ]);

        // Register controllers
        animationManager.registerModelController('player', playerController);
        animationManager.registerModelController('opponent', opponentController);

        console.log('Battle scene initialized successfully!');
      } catch (error) {
        console.error('Failed to setup combatants:', error);
      }
    };

    setupCombatants();

    // Animation loop
    const clock = new THREE.Clock();
    const animate = () => {
      frameIdRef.current = requestAnimationFrame(animate);
      
      const deltaTime = clock.getDelta() * 1000; // Convert to milliseconds
      animationManager.update(deltaTime);
      
      // Camera auto-return
      if (returnToBase && !isInteracting) {
        camera.position.lerp(baseCameraPosition, returnSpeed);
        controls.target.lerp(baseCameraTarget, returnSpeed);
        
        const distanceToBase = camera.position.distanceTo(baseCameraPosition);
        const targetDistanceToBase = controls.target.distanceTo(baseCameraTarget);
        
        if (distanceToBase < 0.1 && targetDistanceToBase < 0.1) {
          camera.position.copy(baseCameraPosition);
          controls.target.copy(baseCameraTarget);
          returnToBase = false;
        }
      }
      
      controls.update();
      renderer.render(scene, camera);
    };

    animate();

    // Cleanup
    return () => {
      if (frameIdRef.current) {
        cancelAnimationFrame(frameIdRef.current);
      }
      if (mountRef.current && renderer) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer?.dispose();
      animationManager?.dispose();
      modelManager?.dispose();
      environmentManager?.dispose();
    };
  }, []); // Remove props dependencies to prevent re-initialization

  // Handle resize
  useEffect(() => {
    if (!cameraRef.current || !rendererRef.current) return;
    
    cameraRef.current.aspect = width / height;
    cameraRef.current.updateProjectionMatrix();
    rendererRef.current.setSize(width, height);
  }, [width, height]);

  // Handle environment model changes
  useEffect(() => {
    if (!environmentManagerRef.current || !sceneRef.current) return;

    const loadEnvironmentModel = async () => {
      const scene = sceneRef.current;
      const environmentManager = environmentManagerRef.current;
      
      if (!scene || !environmentManager) return;

      if (!props.environmentModel?.modelUrl) {
        // Remove environment model if no model is provided
        if (environmentModelRef.current) {
          scene.remove(environmentModelRef.current);
          environmentModelRef.current = null;
        }
        return;
      }

      try {
        console.log('[BattleCanvas] Loading environment model from props change...', {
          modelUrl: props.environmentModel.modelUrl,
          scale: props.environmentModel.scale,
          position: props.environmentModel.position
        });
        const envModel = await environmentManager.loadEnvironmentModel(
          'battle-environment',
          props.environmentModel.modelUrl,
          props.environmentModel.textures || [],
          props.environmentModel.scale || 1,
          props.environmentModel.position
        );

        // Remove old environment model if exists
        if (environmentModelRef.current) {
          scene.remove(environmentModelRef.current);
        }

        // Add new environment model to scene
        environmentModelRef.current = envModel.model;
        scene.add(envModel.model);
        
        console.log('[BattleCanvas] Environment model loaded successfully');
      } catch (error) {
        console.error('[BattleCanvas] Failed to load environment model:', error);
      }
    };

    loadEnvironmentModel();
  }, [props.environmentModel]);

  return (
    <div 
      ref={mountRef} 
      style={{ 
        width: '100%', 
        height: '100%',
        position: 'relative',
        overflow: 'hidden'
      }} 
    >
      {/* EffectsCanvas is now rendered directly in BattleWindow */}
    </div>
  );
});

BattleCanvas.displayName = 'BattleCanvas';

export default BattleCanvas;
